import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Eye, EyeOff, LockKeyhole } from "lucide-react";
import api from "../utils/axiosInstance";
import { useToast } from "../context/ToastContext";
import {
  fieldInputShellClassName,
  fieldInputInnerClassName,
  FIELD_LABEL_CLASS,
  FIELD_ERROR_CLASS,
} from "../utils/formFieldClasses";

export default function SetupPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [submitError, setSubmitError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setSubmitError("");

    if (!token) {
      setSubmitError("Invalid or missing link. Request a new invitation from your administrator.");
      return;
    }

    if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters.");
      return;
    }

    try {
      setLoading(true);

      await api.post("/api/auth/setup-password", { token, password });

      showToast("Password set successfully. You can sign in now.", "success");
      navigate("/");
    } catch (err) {
      setSubmitError(err.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center academic-gradient px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-color">Set or reset password</h1>
          <p className="text-sm text-gray-500 mt-2">
            Choose a new password for your account (invitation or reset link).
          </p>
        </div>

        {submitError ? (
          <div
            className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-medium text-red-600"
            role="alert"
          >
            {submitError}
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="setup-password" className={FIELD_LABEL_CLASS}>
              New Password
              <span className="text-red-500" aria-hidden>
                {" "}
                *
              </span>
            </label>

            <div className={fieldInputShellClassName(Boolean(passwordError))}>
              <LockKeyhole size={18} className="shrink-0 text-slate-400" aria-hidden />
              <input
                id="setup-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError("");
                  setSubmitError("");
                }}
                placeholder="Enter strong password"
                autoComplete="new-password"
                aria-invalid={Boolean(passwordError)}
                aria-describedby={passwordError ? "err-setup-password" : undefined}
                className={fieldInputInnerClassName()}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="shrink-0 text-slate-400 transition hover:text-slate-600"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {passwordError ? (
              <p id="err-setup-password" className={FIELD_ERROR_CLASS} role="alert">
                {passwordError}
              </p>
            ) : null}
          </div>

          <p className="text-xs text-slate-500">Minimum 6 characters required</p>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#2b3896] hover:bg-[#1f2a7a] text-white font-semibold py-3 rounded-lg transition flex items-center justify-center"
          >
            {loading ? (
              <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              "Set Password"
            )}
          </button>
        </form>

        <p className="text-center text-xs text-gray-400 mt-6">
          Secure Setup • SPRR ERP System
        </p>
      </div>
    </div>
  );
}
